import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SpinAnimation } from "@twioku/common_components";
import UndrawMailbox from "../../../assets/undraw_mailbox_re_dvds.svg";
import { Memory } from "../../../api/generated";
import { useAppState, useSetAppState } from "../../../stores/appContext";
import { MemoryItem } from "./MemoryItem";

type Props = {
    memories: Memory[];
    isLoading: boolean;
    emptyMessage: string;
    incrementLikedCount: (memoryId: string) => void;
    tempLikedMemoryIds: string[];
    hasNextPage?: boolean;
    fetchNextPage?: () => void;
    isFetchingNextPage?: boolean;
};

export const MemoriesList: React.FC<Props> = (props: Props) => {
    const {
        memories,
        isLoading,
        emptyMessage,
        incrementLikedCount,
        tempLikedMemoryIds,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = props;
    const appState = useAppState();
    const setAppState = useSetAppState();

    const ref = useRef<HTMLLIElement>(null);
    useEffect(() => {
        if (appState?.goBackMemoryId) {
            if (!ref.current) return;
            ref.current.scrollIntoView({
                behavior: "auto",
                block: "center",
            });
            setAppState((state) => ({
                ...state,
                goBackMemoryId: undefined,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const navigate = useNavigate();
    const gotoMemoryDetail = (memory: Memory) => {
        memory.hasUnreadComment = false;
        memory.isUnread = false;
        setAppState((state) => ({
            ...state,
            goBackMemoryId: memory.id,
        }));
        navigate("/memory/" + memory.id);
    };

    const sentinelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!sentinelRef.current) return;
        if (!fetchNextPage) return;
        const el = sentinelRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry.isIntersecting &&
                    hasNextPage &&
                    !isFetchingNextPage
                ) {
                    fetchNextPage();
                }
            },
            { root: null, rootMargin: "200px", threshold: 0 }
        );
        observer.observe(el);
        return () => observer.unobserve(el);
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    if (isLoading)
        return (
            <div className="absolute inset-0 flex items-center justify-center">
                <SpinAnimation />
            </div>
        );

    return (
        <>
            {memories.length === 0 ? (
                <div className="flex flex-col items-center">
                    <h1 className="mt-12 p-8">{emptyMessage}</h1>
                    <img
                        src={UndrawMailbox}
                        width={250}
                        height={250}
                        className="mt-16"
                    />
                </div>
            ) : (
                <>
                    <ul>
                        {memories.map((memory, index) => (
                            <li
                                key={index}
                                onClick={() => gotoMemoryDetail(memory)}
                                ref={
                                    memory.id === appState.goBackMemoryId
                                        ? ref
                                        : null
                                }
                            >
                                <div>
                                    <MemoryItem
                                        memory={memory}
                                        incrementLikeCount={() =>
                                            incrementLikedCount(memory.id)
                                        }
                                        tempLikedMemoryIds={tempLikedMemoryIds}
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                    {isFetchingNextPage && (
                        <div className="my-2 flex justify-center">
                            <SpinAnimation />
                        </div>
                    )}
                    {fetchNextPage && <div ref={sentinelRef} className="h-1" />}
                </>
            )}
        </>
    );
};
